import { __decorate } from "tslib";
import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { elements } from './sdb-elements.js';
/**
 * A listitem element, used for each individual item within the list element
 *
 * @slot - This is an unnamed slot (default slot)
 * @slot chipbar - This slot is used to hold the chipbar element, only available when using the property 'chipbar'
 * @slot secondaryAction - This slot is used to hold an iconbutton element with a secondary action
 * @slot date - This slot is only available when property date is set. It creates a slot for date listitems
 *
 * @prop {String} overline (part) - optional, used to define an overline (line of text above headline)
 * @prop {String} headline (part) - mandatory, used to define the headline
 * @prop {String} subtitle (part) - optional, used to define a subtitle (line of text below headline)
 * @prop {String} description (part) - optional, used to define a larger piece of text as a description
 *
 * @prop {Boolean} light - optional, used to make text light instead of primary (darkblue)
 * @prop {Boolean} truncate - optional, used to truncate overline, headline, subtitle and description (2 lines)
 *
 * @prop {String} avatar - optional, used to display a profile picture in a circle wrapper (mainly used for items related to specific users)
 * @prop {String} graphic - optional, used to display an image (thumbnail) in a square wrapper
 * @prop {Boolean} gravatar - optional, used to enable a new slot: 'gravatar' used for gravatar items
 * @prop {String} icon - optional, used to display a Fontawesome icon
 *
 * @prop {Boolean} noHover - optional, used to disable all pointer events of this specific listitem element
 * @prop {Boolean} noPadding - optional, displays the listitem without top and bottom margin (used for text-only lists)
 * @prop {Boolean} small - optional, outer height of 40px (instead of 60) and removes secondary action space
 * @prop {Boolean} noSecondaryAction - optional, used to completely remove secondary action space
 * @prop {String} centerIcons - optional, used to center align icons (vertically) if displayed in the listitem
 * @prop {Boolean} date - optional, used for (multiline) date items (e.g. catalog planner) + new slot
 *
 * @prop {Boolean} disabled - optional, disables listitem (no pointer events, opacity)
 * @prop {Boolean} disabledText - optional, used to display a label on top of disabled item with more information
 * @prop {Boolean} locked - optional, used to lock listitem (lock icon, opacity)
 *
 * @prop {String} statusColor - optional, used to display statusbar in specified color (green, orange, red, blue and grey).
 * @prop {Boolean} statusBackground - optional, used to display listitem in background matching statusColor (default: false).
 *
 * @prop {Boolean} checkable - optional, used to make listitem checkable with a checkbox
 * @prop {Boolean} checked - if checkable is used, checked will display the checkbox as checked
 * @prop {Boolean} disabledCheckbox - if checkable is used, this option will dislpay a disabled checkbox
 */
let SDBContentListItem = class SDBContentListItem extends LitElement {
    constructor() {
        super(...arguments);
        this.overline = '';
        this.headline = '';
        this.subtitle = '';
        this.description = '';
        this.graphic = '';
        this.avatar = '';
        this.disabledText = '';
        this.icon = '';
        this.gravatar = false;
        this.light = false;
        this.locked = false;
        this.noPadding = false;
        this.small = false;
        this.date = false;
        this.noHover = false;
        this.checkable = false;
        this.checked = false;
        this.disabledCheckbox = false;
        this.statusColor = '';
        this.statusBackground = false;
    }
    render() {
        return html `
            ${this.disabledText
            ? html ` <div class="disabledTextWrapper">
                      <span>${this.disabledText}</span>
                  </div>`
            : html ``}
            ${this.locked
            ? html ` <div class="lockedWrapper">
                      <sdb-content-icon
                          icon="fal fa-lock"
                          size="30"
                      ></sdb-content-icon>
                  </div>`
            : html ``}
            <div class="outer" part="outer">
                <div class="statusBar ${this.statusColor}"></div>
                <!-- Listitem content (text & chipbar) -->
                <div class="listItemContentWrapper">
                    <section class="mainContent">
                        ${this.checkable
            ? html `<div class="listCheckboxWrapper">
                                  <div class="listCheckbox">
                                      <sdb-content-checkbox
                                          ?disabled=${this.disabledCheckbox}
                                          ?checked=${this.checked}
                                          @checked-changed="${(e) => {
                this._checkedChanged(e);
            }}"
                                      >
                                      </sdb-content-checkbox>
                                  </div>
                              </div>`
            : html ``}
                        <!-- Listitem images yes/no -->
                        ${this.graphic
            ? html ` <div class="ListItemImgWrapper">
                                  <div
                                      style="background-image: url('${this
                .graphic}');"
                                      class="listItemGraphic"
                                  ></div>
                              </div>`
            : html ``}
                        ${this.avatar
            ? html ` <div class="ListItemImgWrapper">
                                  <div
                                      style="background-image: url('${this
                .avatar}');"
                                      class="listItemAvatar"
                                  ></div>
                              </div>`
            : html ``}
                        ${this.icon
            ? html ` <div class="ListItemImgWrapper">
                                  <div class="listItemIcon">
                                      <sdb-content-icon
                                          size="32"
                                          .icon=${this.icon}
                                      ></sdb-content-icon>
                                  </div>
                              </div>`
            : html ``}
                        ${this.gravatar
            ? html ` <div class="ListItemImgWrapper">
                                  <div class="listItemGravatar">
                                      <slot name="gravatar"></slot>
                                  </div>
                              </div>`
            : html ``}
                        <div class="main" part="main">
                            ${this.overline
            ? html ` <div class="overline" part="overline">
                                      ${this.overline}
                                  </div>`
            : html ``}
                            <div class="headline" part="headline">
                                ${this.headline}
                            </div>
                            <!-- headline is mandatory, so always displayed -->
                            ${this.subtitle
            ? html ` <div class="subtitle" part="subtitle">
                                      ${this.subtitle}
                                  </div>`
            : html ``}
                            ${this.description
            ? html ` <div
                                      class="description"
                                      part="description"
                                  >
                                      ${this.description}
                                  </div>`
            : html ``}
                            ${this.date
            ? html ` <div class="date" part="date">
                                      <slot name="date"></slot>
                                  </div>`
            : html ``}
                        </div>
                    </section>
                    <slot class="listItemColumn" name="2"></slot>
                    <slot class="listItemColumn" name="3"></slot>
                    <slot class="listItemColumn" name="4"></slot>
                </div>
                <!-- Secondary Action -->

                <div class="secondaryAction">
                    <slot
                        name="secondaryAction"
                        @click=${this._stopPropagation}
                    ></slot>
                </div>

                <div id="listItemChipbar">
                    <slot name="chipbar" class="chipbar"></slot>
                </div>

                <div id="defaultSlot">
                    <slot @click=${this._stopPropagation}></slot>
                </div>
            </div>
        `;
    }
    _stopPropagation(e) {
        e.stopPropagation();
    }
    _checkedChanged(e) {
        this.checked = e.detail.value;
        this.dispatchEvent(new CustomEvent('checked-changed', {
            detail: e.detail,
        }));
    }
};
SDBContentListItem.styles = [
    elements,
    css `
            :host {
                display: block;
                position: relative;
                border-top-left-radius: 2px;
                border-bottom-left-radius: 2px;
                margin-bottom: var(--sdb-list-status, 0);
                background: var(--sdb-content-listitem-background, white);
            }

            :host .outer {
                border-bottom: 1px solid var(--sdb-list-separator, transparent);
            }

            .outer {
                background: transparent;
                position: relative;
                cursor: pointer;
                height: calc(
                    100% - 31px
                ); /* height - 2x 15px padding - 1px border */
                min-height: 60px;
                padding: 15px 0 15px 10px;
                color: var(--sdb-content-listitem-outer-color, --text-primary);
                display: grid;
                align-items: center;
                grid-template-rows: 1fr; /* rows will use full available height */
                grid-template-columns: 1fr 50px;
                grid-template-areas:
                    'content secondary'
                    'chipbar chipbar'
                    'slot slot';
            }

            :host([small]) .outer {
                height: 40px;
                min-height: 40px;
                grid-template-columns: 1fr;
                grid-template-areas:
                    'content'
                    'chipbar'
                    'slot';
            }

            :host([noPadding]) .outer {
                padding: 0 0 0 10px;
            }

            :host([light]) .outer {
                color: var(--text-light);
            }

            :host([noSecondaryAction]) .outer {
                padding: 15px 20px 15px 10px;
                grid-template-columns: 1fr;
                grid-template-areas:
                    'content'
                    'chipbar'
                    'slot';
            }

            :host(:hover) .outer {
                background: rgba(0, 0, 0, 0.05);
            }

            :host([disabled]) {
                pointer-events: none;
            }

            :host([disabled]) .outer {
                cursor: default;
                opacity: 0.4;
            }

            :host([disabledText]) .outer {
                opacity: 0.2 !important;
            }

            :host([truncate]) section.mainContent div.main div {
                display: -webkit-box;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;
                overflow: hidden;
            }

            :host([disabled]) .listItemContent {
                color: var(--element-disabled);
            }

            :host([disabled]:hover) .outer {
                background: transparent;
            }

            .listItemContentWrapper {
                grid-area: content;
                display: grid;
                grid-auto-columns: 1fr; /* columns will use full available width */
                grid-auto-flow: column;
                justify-content: flex-start; /* makes sure the content is left aligned */
                align-items: stretch; /* makes sure the items use the full available height of the row */
                gap: 0 15px;
            }

            .listItemContentWrapper > * {
                /* makes sure all the optional items use the full width of the listitem content wrapper */
                width: 100%;
            }

            :host .statusBar {
                display: block;
                height: 100%;
                width: 10px;
                background: transparent;
                position: absolute;
                top: 0;
                left: 0;
                border-bottom: 1px solid transparent;
                border-top-left-radius: 2px;
                border-bottom-left-radius: 2px;
            }

            .statusBar.green {
                background: var(--success-color);
            }

            :host([statusBackground][statusColor='green']) {
                background: var(--success-background);
            }

            /* :host([greenStatus]) .mainContent, :host([greenStatus]) .secondaryAction, :host([greenStatus]) .chipbar{
        opacity:0.7;
    } */

            .statusBar.orange {
                background: var(--warning-color);
            }

            :host([statusBackground][statusColor='orange']) {
                background: var(--warning-background);
            }

            .statusBar.red {
                background: var(--alert-color);
            }

            :host([statusBackground][statusColor='red']) {
                background: var(--alert-background);
            }

            .statusBar.blue {
                background: var(--inprogress-color);
            }

            :host([statusBackground][statusColor='blue']) {
                background: var(--inprogress-background);
            }

            .statusBar.grey {
                background: var(--element-darkgrey);
            }

            :host([statusBackground][statusColor='grey']) {
                background: var(--element-darkgrey);
            }

            :host([locked]) .outer {
                opacity: 0.3;
            }

            :host([locked]) .lockedWrapper {
                display: flex;
                width: 100%;
                height: 100%;
                position: absolute;
                justify-content: center;
                align-items: center;
            }

            :host .disabledTextWrapper {
                display: none;
            }

            :host([disabled]) .disabledTextWrapper {
                display: flex;
                width: 100%;
                height: 100%;
                position: absolute;
                justify-content: center;
                align-items: center;
            }

            :host([disabled]) .disabledTextWrapper > span {
                display: block;
                background: var(--element-lightgrey);
                color: #999;
                padding: 7.5px 15px;
                border-radius: 5px;
                z-index: 1;
            }

            section.image {
                width: 60px;
                display: inline-flex;
            }

            section.mainContent {
                display: grid;
                grid-template-columns: auto auto 1fr;
                grid-template-areas: 'checkbox image main';
            }

            section.mainContent .main {
                grid-area: main;
                display: flex;
                justify-content: var(--sdb-listitem-main-justify, center);
                flex-direction: column;
                padding-left: 10px;
                overflow-x: hidden;
            }

            :host([date]) .outer {
                padding: 0 0 0 10px;
                min-height: 22px;
            }

            :host([date]:hover) .outer {
                background: transparent;
            }

            .listItemColumn::slotted(*) {
                padding: 0 15px;
                align-self: var(--sdb-listitem-content-align, center);
            }

            .listItemColumn > * {
                /* makes sure all the optional items use the full width of the accordionItem content wrapper */
                width: 100%;
            }

            .secondaryAction {
                grid-area: secondary;
                display: grid;
                justify-items: center;
                align-items: center;
            }

            .ListItemImgWrapper {
                grid-area: image;
                display: flex;
                justify-content: var(--sdb-listitem-icon-justify, center);
                align-items: var(--sdb-listitem-icon-align, start);
                min-width: 70px;
            }

            :host([centerIcons]) .ListItemImgWrapper {
                --sdb-listitem-icon-justify: center;
                --sdb-listitem-icon-align: center;
            }

            .listItemAvatar,
            .listItemGraphic,
            .listItemIcon,
            .listItemGravatar {
                grid-template-areas: image;
                width: 60px;
                height: 60px;
                margin: 3px 0 0 var(--sdb-listitem-icon-padding-left, 10px);
                background-size: cover;
                background-position: center center;
                justify-content: start;
            }

            .listItemGravatar ::slotted(img) {
                max-width: 60px;
            }

            .listItemIcon {
                width: 60px;
                height: 40px;
            }

            #listItemChipbar {
                grid-area: chipbar;
                display: flex;
                padding-left: 10px;
                padding-right: 10px;
            }

            :host([icon]) #listItemChipbar,
            :host([graphic]) #listItemChipbar,
            :host([avatar]) #listItemChipbar,
            :host([gravatar]) #listItemChipbar {
                margin-left: calc(
                    var(--chipMarginLeft, 0px) + var(--chipExtraMarginLeft, 0px)
                );
                --chipMarginLeft: 70px;
            }

            :host([checkable]) #listItemChipbar {
                margin-left: calc(
                    var(--chipMarginLeft, 0px) + var(--chipExtraMarginLeft, 0px)
                );
                --chipExtraMarginLeft: 40px;
            }

            .listCheckboxWrapper {
                grid-area: checkbox;
                display: flex;
                width: auto;
                align-items: center;
            }

            .listCheckbox {
                width: 30px;
                margin-top: 3px;
                padding-left: 10px;
                display: flex;
                justify-content: center;
            }

            .listItemIcon {
                background: transparent;
                display: flex;
                justify-content: var(--sdb-listitem-icon-justify, center);
                align-items: var(--sdb-listitem-icon-align, start);
            }

            .listItemAvatar {
                border-radius: 50%; /*makes sure user avatars are displayed in circle instead of square*/
            }

            .overline,
            .headline,
            .subtitle,
            .description,
            .chipbar {
                display: flex;
                align-items: center;
            }

            .overline {
                font-size: 0.85em;
                text-transform: uppercase;
            }

            .description {
                margin: 15px 15px 15px 0;
                font-weight: 300;
            }

            .overline + .headline {
                /* makes sure the overline is 'glued on top of' the headline if only these 2 are used */
                align-items: flex-start;
            }

            .headline + .subtitle {
                align-items: flex-start;
            }

            .overline,
            .subtitle,
            .description {
                font-weight: 300;
            }

            #defaultSlot {
                grid-area: slot;
                padding-left: 10px;
            }

            :host([noHover]:hover) .outer {
                cursor: default;
                background: transparent;
            }

            :host([locked]:hover) .outer {
                background: transparent;
            }

            @media only screen and (max-width: 767px) {
                /* For mobile phones: */
                .listItemContentWrapper {
                    grid-auto-flow: row;
                }

                :host([date]) .listItemContentWrapper {
                    margin-bottom: 15px;
                }

                :host([date]) .main {
                    padding-left: 0;
                }

                :host {
                    grid-template-columns: 1fr 25px;
                }

                .listItemColumn::slotted(*) {
                    padding: 0px 10px 0 0;
                }
            }
        `,
];
__decorate([
    property({ type: String })
], SDBContentListItem.prototype, "overline", void 0);
__decorate([
    property({ type: String })
], SDBContentListItem.prototype, "headline", void 0);
__decorate([
    property({ type: String })
], SDBContentListItem.prototype, "subtitle", void 0);
__decorate([
    property({ type: String })
], SDBContentListItem.prototype, "description", void 0);
__decorate([
    property({ type: String })
], SDBContentListItem.prototype, "graphic", void 0);
__decorate([
    property({ type: String })
], SDBContentListItem.prototype, "avatar", void 0);
__decorate([
    property({ type: String })
], SDBContentListItem.prototype, "disabledText", void 0);
__decorate([
    property({ type: String })
], SDBContentListItem.prototype, "icon", void 0);
__decorate([
    property({ type: Boolean })
], SDBContentListItem.prototype, "gravatar", void 0);
__decorate([
    property({ type: Boolean })
], SDBContentListItem.prototype, "light", void 0);
__decorate([
    property({ type: Boolean })
], SDBContentListItem.prototype, "locked", void 0);
__decorate([
    property({ type: Boolean })
], SDBContentListItem.prototype, "noPadding", void 0);
__decorate([
    property({ type: Boolean })
], SDBContentListItem.prototype, "small", void 0);
__decorate([
    property({ type: Boolean })
], SDBContentListItem.prototype, "date", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], SDBContentListItem.prototype, "noHover", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], SDBContentListItem.prototype, "checkable", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], SDBContentListItem.prototype, "checked", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], SDBContentListItem.prototype, "disabledCheckbox", void 0);
__decorate([
    property({ type: String, reflect: true })
], SDBContentListItem.prototype, "statusColor", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], SDBContentListItem.prototype, "statusBackground", void 0);
SDBContentListItem = __decorate([
    customElement('sdb-content-listitem')
], SDBContentListItem);
export { SDBContentListItem };
//# sourceMappingURL=sdb-content-listitem.js.map